'use client';

import React, { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ImpersonationTarget } from '@/features/admin-geral/types/operacoes';
// eslint-disable-next-line no-restricted-imports -- same feature (admin-geral) internal import
import { adminImpersonateService } from '@/features/admin-geral/store/impersonate/services';
import { IMPERSONATION_STORAGE_KEY, removeStoredWorkspaceId, setStoredWorkspaceId } from '@/shared/utils/workspace';
import { buscarEscritorioPorId, clearCurrentEscritorio } from '@/features/escritorio';
import { loadAuthSession } from '@/features/auth';
import { useAppDispatch } from '@/store/hooks';
const STORAGE_KEY = IMPERSONATION_STORAGE_KEY;
interface ImpersonationState {
  isImpersonating: boolean;
  targetUserId: string | null;
  targetUserNome: string | null;
  targetUserEmail: string | null;
  targetTenantId: string | null;
  targetTenantNome: string | null;
  previousEscritorioId: string | null;
}
interface ImpersonationContextValue extends ImpersonationState {
  startImpersonation: (target: ImpersonationTarget) => Promise<boolean>;
  stopImpersonation: () => Promise<void>;
}
const initialState: ImpersonationState = {
  isImpersonating: false,
  targetUserId: null,
  targetUserNome: null,
  targetUserEmail: null,
  targetTenantId: null,
  targetTenantNome: null,
  previousEscritorioId: null
};
function loadPersistedState(): ImpersonationState {
  if (typeof window === 'undefined') return initialState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState;
    return JSON.parse(raw) as ImpersonationState;
  } catch {
    return initialState;
  }
}
function aplicarWorkspaceGuardado(workspaceId: string | null) {
  if (workspaceId) {
    setStoredWorkspaceId(workspaceId);
    return;
  }
  removeStoredWorkspaceId();
}
function persistState(state: ImpersonationState) {
  if (typeof window === 'undefined') return;
  try {
    if (state.isImpersonating) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore
  }
}
export const ImpersonationContext = createContext<ImpersonationContextValue>({
  ...initialState,
  startImpersonation: async () => false,
  stopImpersonation: async () => {}
});
interface ImpersonationProviderProps {
  children: React.ReactNode;
}
export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({
  children
}) => {
  const dispatch = useAppDispatch();
  const [state, setState] = useState<ImpersonationState>(loadPersistedState);

  // Persist changes
  useEffect(() => {
    persistState(state);
  }, [state]);

  // `papelEscritorio` não vem do token: o backend o resolve a cada request pelo header
  // `X-Workspace-Id`. Trocar de workspace sem reler a sessão deixa o Redux com o papel do
  // workspace anterior — o admin entrava no tenant alheio como EDITOR e batia em porta fechada
  // no financeiro. Entrada e saída passam por aqui para que a regra não seja esquecida num dos
  // dois lados, que foi exatamente como o bug nasceu.
  const ativarWorkspaceEReleSessao = useCallback(async (workspaceId: string | null): Promise<void> => {
    aplicarWorkspaceGuardado(workspaceId);
    await dispatch(loadAuthSession()).unwrap();
  }, [dispatch]);
  const startImpersonation = useCallback(async (target: ImpersonationTarget): Promise<boolean> => {
    try {
      // Call backend to validate + audit
      await adminImpersonateService.workspace({
        escritorioId: target.tenantId,
        motivo: `Visualização administrativa: ${target.userNome} (${target.tenantNome})`
      });

      // Save current escritório to restore later
      const currentEscritorioId = typeof window !== 'undefined' ? window.localStorage.getItem('currentEscritorioId') : null;

      // Entrar sem o papel novo é o próprio bug, então a releitura é etapa obrigatória: se ela
      // não vier, desfaz a troca e devolve `false` — o chamador já avisa o admin. Esperá-la
      // antes do redirect também evita a tela de destino renderizar com o papel velho.
      try {
        await ativarWorkspaceEReleSessao(target.tenantId);
      } catch {
        aplicarWorkspaceGuardado(currentEscritorioId);
        return false;
      }
      await dispatch(buscarEscritorioPorId(target.tenantId)).unwrap().catch(() => undefined);
      const newState: ImpersonationState = {
        isImpersonating: true,
        targetUserId: target.userId,
        targetUserNome: target.userNome,
        targetUserEmail: target.userEmail,
        targetTenantId: target.tenantId,
        targetTenantNome: target.tenantNome,
        previousEscritorioId: currentEscritorioId
      };
      setState(newState);
      return true;
    } catch {
      return false;
    }
  }, [dispatch, ativarWorkspaceEReleSessao]);
  const stopImpersonation = useCallback(async (): Promise<void> => {
    dispatch(clearCurrentEscritorio());
    setState(initialState);

    // Sair carregando o MASTER_ADMIN ganho no tenant alheio é o mesmo defeito ao contrário, e por
    // isso a releitura é aguardada antes de quem chamou navegar. Diferente da entrada, falhar aqui
    // não pode cancelar a saída: o admin já está fora, e o backend segue reavaliando o papel a
    // cada request pelo header — o pior caso é a UI mostrar o papel velho até o próximo `/auth/me`.
    await ativarWorkspaceEReleSessao(state.previousEscritorioId).catch(() => undefined);
    if (state.previousEscritorioId) {
      await dispatch(buscarEscritorioPorId(state.previousEscritorioId)).unwrap().catch(() => undefined);
    }
  }, [state.previousEscritorioId, dispatch, ativarWorkspaceEReleSessao]);
  const value = useMemo<ImpersonationContextValue>(() => ({
    ...state,
    startImpersonation,
    stopImpersonation
  }), [state, startImpersonation, stopImpersonation]);
  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};